/**
 * Local-date helpers.
 *
 * Use these instead of `date.toISOString().slice(0, 10)` whenever you want a
 * "calendar day" string (YYYY-MM-DD) that matches what the user sees on their
 * wall clock. `.toISOString()` converts to UTC, which silently shifts the date
 * by ±1 day for any user east of UTC (UK BST, Europe, Asia, etc.) — that bug
 * caused the dispatch board to display every job under the wrong day column.
 *
 * Display formatting: the platform shows every date as dd/mm/yyyy. Use fmtDate /
 * fmtDateTime for anything a person reads; keep YYYY-MM-DD only for <DateInput>
 * values, API payloads and comparisons.
 */

export function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Format a Date to YYYY-MM-DD using local calendar components (no UTC shift). */
export function toLocalISODate(d = new Date()) {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}

/** Today's date in the user's local calendar, formatted YYYY-MM-DD. */
export function todayLocalISO() {
  return toLocalISODate(new Date());
}

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const pad2 = (n) => String(n).padStart(2, "0");

function toDate(v) {
  if (v === null || v === undefined || v === "") return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  const s = String(v).trim();
  const m = ISO_DAY.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return isNaN(d) ? null : d;
}

/** "2026-09-17" / ISO datetime / Date → "17/09/2026". Unparseable input is returned as-is; empty → "". */
export function fmtDate(v) {
  const d = toDate(v);
  if (!d) return v ? String(v) : "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** ISO datetime / Date → "17/09/2026 14:05" (local time). */
export function fmtDateTime(v) {
  const d = toDate(v);
  if (!d) return v ? String(v) : "";
  return `${fmtDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

const DATE_KEYS = ["day", "month", "year", "weekday"];
const TIME_KEYS = ["hour", "minute", "second"];

/**
 * Platform-wide dd/mm/yyyy: patch Date#toLocaleDateString / #toLocaleString so every existing call site
 * (200+ of them, with assorted locale/option combos) renders "17/09/2026", keeping any weekday and time parts.
 * Month-only labels (e.g. chart axes `{ month: "short" }`) are left to the native formatter.
 */
export function installUkDateFormat() {
  if (typeof Date === "undefined" || Date.prototype.__ukFormat) return;
  const nativeDateString = Date.prototype.toLocaleDateString;
  const nativeString = Date.prototype.toLocaleString;

  const render = (d, opts, withTime) => {
    if (isNaN(d)) return "Invalid Date";
    const o = opts || {};
    const hasDatePart = DATE_KEYS.some((k) => k in o);
    const hasTimePart = TIME_KEYS.some((k) => k in o);
    if (hasDatePart && !("day" in o)) return null; // month/year/weekday-only labels → native
    const tz = o.timeZone;
    let y, mo, da, h, mi, wd = "";
    if (tz) {
      const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false, weekday: o.weekday || undefined }).formatToParts(d);
      const g = (t) => (parts.find((p) => p.type === t) || {}).value || "";
      y = g("year"); mo = g("month"); da = g("day"); h = g("hour"); mi = g("minute"); wd = g("weekday");
      if (h === "24") h = "00";
    } else {
      y = d.getFullYear(); mo = pad2(d.getMonth() + 1); da = pad2(d.getDate()); h = pad2(d.getHours()); mi = pad2(d.getMinutes());
      if (o.weekday) wd = nativeDateString.call(d, "en-GB", { weekday: o.weekday });
    }
    const time = `${h}:${mi}${"second" in o ? `:${pad2(d.getSeconds())}` : ""}`;
    if (hasTimePart && !hasDatePart) return time; // time-only request
    let out = `${da}/${mo}/${y}`;
    if (o.weekday && wd) out = `${wd} ${out}`;
    if (hasTimePart || (withTime && !hasDatePart)) out += ` ${time}`;
    return out;
  };

  Date.prototype.toLocaleDateString = function (locale, opts) {
    const r = render(this, opts, false);
    return r === null ? nativeDateString.call(this, locale, opts) : r;
  };
  Date.prototype.toLocaleString = function (locale, opts) {
    const r = render(this, opts, true);
    return r === null ? nativeString.call(this, locale, opts) : r;
  };
  Date.prototype.__ukFormat = true;
}
